import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cogoToast from 'cogo-toast';
import slugify from 'slugify';
import _ from 'lodash';
import Select from 'react-select';
import classnames from 'classnames';
import './HierarchyEdit.scss';

import {
  addFeature,
  updateCategory,
  updateDepartment,
  updateTag,
  updateIndustry,
  deleteCategory,
  deleteDepartment,
  deleteTag,
  deleteFeature,
  deleteIndustry
} from '../../../api/catalog';
import { getRootSMSUrl } from '../../../helpers/helpers';

class HierarchyEdit extends Component {
  static propTypes = {
    catalog: PropTypes.object.isRequired,
    syncCatalogHierarchy: PropTypes.func.isRequired,
    selectItem: PropTypes.func.isRequired,
    selectedItem: PropTypes.object,
    selectedItemType: PropTypes.string
  };

  componentDidUpdate(prevProps) {
    const itemChanged = _.get(this.props, 'selectedItem.id') !== _.get(prevProps, 'selectedItem.id');
    const typeChanged = _.get(this.props, 'selectedItemType') !== _.get(prevProps, 'selectedItemType');
    if (itemChanged || typeChanged) {
      this.setState({
        name: _.get(this.props.selectedItem, 'name') || '',
        seoTitle: _.get(this.props.selectedItem, 'seoTitle') || '',
        seoDescription: _.get(this.props.selectedItem, 'seoDescription') || '',
        seoDescriptionLong: _.get(this.props.selectedItem, 'seoDescriptionLong') || '',
        synonyms: _.get(this.props.selectedItem, 'synonyms') || '',
        urlStub: _.get(this.props.selectedItem, 'urlStub') || '',
        isFilter: _.get(this.props.selectedItem, 'isFilter', false),
        isFeatured: !_.isEmpty(_.get(this.props.selectedItem, 'feature', false)),
        isHidden: _.get(this.props.selectedItem, 'isHidden', false),
        isHiddenForAllProductResults: _.get(this.props.selectedItem, 'isHiddenForAllProductResults', false),
        isAutoTag: _.get(this.props.selectedItem, 'isAutoTag', false),
        Category_id: _.get(this.props.selectedItem, 'Category_id') || null,
        Department_id: _.get(this.props.selectedItem, 'Department_id') || null,
        Industry_id: _.get(this.props.selectedItem, 'Industry_id') || null,
        autoTagShopPercentageMin: _.get(this.props.selectedItem, 'autoTagShopPercentageMin', 30),
        shopProductResultsPageImageFormat: _.get(this.props.selectedItem, 'shopProductResultsPageImageFormat', 'default'),
        googleProductCategory: _.get(this.props.selectedItem, 'googleProductCategory') || ''
      });
    }
  }

  deleteItem = () => {
    const { selectedItem } = this.props;
    const deleteFunction = this.isDepartment()
      ? deleteDepartment
      : this.isCategory()
      ? deleteCategory
      : this.isTag()
      ? deleteTag
      : this.isIndustry()
      ? deleteIndustry
      : null;

    const areYouSure = window.confirm(`Are you sure you want to delete ${selectedItem.name}?`);

    deleteFunction &&
      areYouSure &&
      deleteFunction(selectedItem.id).then(
        resp => {
          this.props.syncCatalogHierarchy();
          this.props.selectItem();
        },
        err => {
          console.error(err);
          cogoToast.error(JSON.stringify(err));
        }
      );
  };

  isCategory = () => this.props.selectedItemType === 'category';
  isDepartment = () => this.props.selectedItemType === 'department';
  isTag = () => this.props.selectedItemType === 'tag';
  isIndustry = () => this.props.selectedItemType === 'industry';

  updateItem = async () => {
    const { selectedItem } = this.props;
    const {
      name,
      seoTitle,
      seoDescription,
      seoDescriptionLong,
      synonyms,
      urlStub,
      isFilter,
      isHidden,
      isHiddenForAllProductResults,
      isAutoTag,
      autoTagShopPercentageMin,
      shopProductResultsPageImageFormat,
      googleProductCategory,
      isFeatured
    } = this.state;

    let updateFunction, featureData;
    if (this.isCategory()) {
      updateFunction = updateCategory;
      featureData = { Category_id: selectedItem.id };
    } else if (this.isDepartment()) {
      updateFunction = updateDepartment;
      featureData = { Department_id: selectedItem.id };
    } else if (this.isTag()) {
      updateFunction = updateTag;
      featureData = { Tag_id: selectedItem.id };
    } else if (this.isIndustry()) {
      updateFunction = updateIndustry;
      featureData = { Industry_id: selectedItem.id };
    }

    const newData = {};

    const wantsToChangeName = name.length && name !== selectedItem.name;
    if (wantsToChangeName) {
      newData.name = name;
    }

    const wantsToChangeTitle =
      (seoTitle.length && seoTitle !== selectedItem.seoTitle) || // Updating title
      (!seoTitle.length && selectedItem.seoTitle); // Removing title
    if (wantsToChangeTitle) {
      newData.seoTitle = seoTitle;
    }

    const wantsToChangeDescription =
      (seoDescription.length && seoDescription !== selectedItem.seoDescription) || // Updating description
      (!seoDescription.length && selectedItem.seoDescription); // Removing description
    if (wantsToChangeDescription) {
      newData.seoDescription = seoDescription;
    }

    const wantsToChangeLongDescription =
      (seoDescriptionLong.length && seoDescription !== selectedItem.seoDescriptionLong) || // Updating description
      (!seoDescriptionLong.length && selectedItem.seoDescriptionLong); // Removing description
    if (wantsToChangeLongDescription) {
      newData.seoDescriptionLong = seoDescriptionLong;
    }

    const wantsToChangeSynonyms = (synonyms?.length && synonyms !== selectedItem.synonyms) || (!synonyms?.length && selectedItem.synonyms);
    if (wantsToChangeSynonyms) {
      newData.synonyms = synonyms;
    }

    const wantsToChangeUrlStub = (urlStub.length && urlStub !== selectedItem.urlStub) || (!urlStub.length && selectedItem.urlStub);
    if (wantsToChangeUrlStub) {
      newData.urlStub = urlStub;
    }

    const wantsToChangeFilter = !_.isNil(selectedItem.isFilter) && isFilter !== selectedItem.filter; // Only if filter is set on the object (in the case of a tag)
    if (wantsToChangeFilter) {
      newData.isFilter = isFilter;
    }

    const wantsToChangeVisibility = isHidden !== selectedItem.isHidden;
    if (wantsToChangeVisibility) {
      newData.isHidden = isHidden;
    }

    const wantsToChangeVisibilityForAllProductResults =
      this.isCategory() && isHiddenForAllProductResults !== selectedItem.isHiddenForAllProductResults;
    if (wantsToChangeVisibilityForAllProductResults) {
      newData.isHiddenForAllProductResults = isHiddenForAllProductResults;
    }

    const wantsToChangeAutoTagging = isAutoTag !== selectedItem.isAutoTag;
    if (wantsToChangeAutoTagging) {
      newData.isAutoTag = isAutoTag;
    }

    const wantsToChangeAutoTagMin = String(autoTagShopPercentageMin) !== String(selectedItem.autoTagShopPercentageMin);
    if (wantsToChangeAutoTagMin) {
      newData.autoTagShopPercentageMin = parseInt(autoTagShopPercentageMin);
    }

    const wantsToChangeFeature = (_.isEmpty(selectedItem.feature) && isFeatured) || (!_.isEmpty(selectedItem.feature) && !isFeatured);

    const wantsToChangeshopProductResultsPageImageFormat = shopProductResultsPageImageFormat !== selectedItem.shopProductResultsPageImageFormat;
    if (wantsToChangeshopProductResultsPageImageFormat) {
      newData.shopProductResultsPageImageFormat = shopProductResultsPageImageFormat;
    }

    const wantsToChangeGoogleProductCategory =
      this.isDepartment() &&
      ((googleProductCategory?.length && googleProductCategory !== selectedItem.googleProductCategory) ||
        (!googleProductCategory?.length && selectedItem.googleProductCategory));
    if (wantsToChangeGoogleProductCategory) {
      newData.googleProductCategory = googleProductCategory || null;
    }

    if (_.isEmpty(newData) && !wantsToChangeFeature) {
      cogoToast.info('Nothing to update.');
      return;
    }

    this.setState({ isSaving: true });
    try {
      updateFunction && !_.isEmpty(newData) && (await updateFunction(selectedItem.id, newData));
      if (wantsToChangeFeature) {
        isFeatured ? await addFeature(featureData) : await deleteFeature(selectedItem.feature.id);
      }
      cogoToast.success('Successful Update, resyncing hierarchy...');
    } catch (error) {
      cogoToast.error(JSON.stringify(error) || 'An error occurred while updating the item, please check the network tab for more details.');
    }

    await this.props.syncCatalogHierarchy();
    this.setState({ isSaving: false });
  };

  state = {
    name: '',
    seoTitle: '',
    seoDescription: '',
    seoDescriptionLong: '',
    Department_id: null,
    Category_id: null,
    Industry_id: null,
    urlStub: '',
    isFilter: false,
    isSaving: false,
    isFeatured: false,
    isHidden: false,
    isHiddenForAllProductResults: false,
    isAutoTag: false,
    autoTagShopPercentageMin: 30,
    googleProductCategory: ''
  };

  getDepartmentOptions = () => {
    const { catalog, selectedItem } = this.props;
    const { showAllDepartments } = this.state;
    const { industries } = catalog;
    const industriesById = _.keyBy(industries, 'id');
    const activeDepartment = _.find(industries, i => i.departments.find(d => d.id === selectedItem.Department_id));
    const allDepartments = industries.map(i => i.departments).flat();
    const departmentsToShow = showAllDepartments ? allDepartments : activeDepartment?.departments || [];
    return (
      departmentsToShow.map(department => ({ value: department.id, label: `${industriesById[department.Industry_id].name} - ${department.name}` })) ||
      []
    );
  };

  getIndustryOptions = () => {
    const { catalog } = this.props;
    const { industries } = catalog;
    return industries.map(industry => ({ value: industry.id, label: industry.name }));
  };

  getShopSearchResultsPageUrl = () => {
    let baseUrl = getRootSMSUrl();
    if (this.isCategory()) baseUrl += `/shop?Category_id=${this.state.Category_id}`;
    if (this.isDepartment()) baseUrl += `/shop?Department_id=${this.state.Department_id}`;
    if (this.isIndustry()) baseUrl += `/shop?Industry_id=${this.state.Industry_id}`;
    return baseUrl;
  };

  render() {
    const { selectedItem, selectedItemType } = this.props;
    const {
      name,
      seoTitle,
      seoDescription,
      seoDescriptionLong,
      synonyms,
      urlStub,
      isFilter,
      isSaving,
      isFeatured,
      isHidden,
      isHiddenForAllProductResults,
      isAutoTag,
      autoTagShopPercentageMin,
      googleProductCategory
    } = this.state;

    return (
      <div className='hierarchy-edit-outer-container panel'>
        {selectedItem ? (
          <div className='sections'>
            <div className='section'>
              <div className='section-label'>General Information for {name}</div>
              <input placeholder='Name' value={name} onChange={({ target }) => this.setState({ name: target.value })} />
              {this.isDepartment() && (
                <Select
                  className='select'
                  placeholder='Industry'
                  menuPlacement='top'
                  value={this.getIndustryOptions().find(({ value }) => value === this.state.Industry_id)}
                  options={this.getIndustryOptions()}
                  onChange={resp => {
                    this.setState({ Industry_id: resp.value });
                    updateDepartment(selectedItem.id, { Industry_id: resp.value });
                    this.props.syncCatalogHierarchy();
                  }}
                />
              )}
              {this.isCategory() && (
                <>
                  <Select
                    className='select'
                    placeholder='Department'
                    menuPlacement='top'
                    value={this.getDepartmentOptions().find(({ value }) => value === this.state.Department_id)}
                    options={this.getDepartmentOptions()}
                    onChange={resp => {
                      this.setState({ Department_id: resp.value });
                      updateCategory(selectedItem.id, { Department_id: resp.value });
                      this.props.syncCatalogHierarchy();
                    }}
                  />
                  <div onClick={() => this.setState({ showAllDepartments: !this.state.showAllDepartments })} className='show-all-from-select'>
                    {this.state.showAllDepartments ? 'Hide All Departments' : 'Show All Departments'}
                  </div>
                </>
              )}
            </div>
            <div className='section'>
              <div className='section-label'>SEO</div>
              <input placeholder={`Best in ${name}`} value={seoTitle} onChange={({ target }) => this.setState({ seoTitle: target.value })} />
              <textarea
                placeholder='Keyword Optimized SEO Description'
                value={seoDescription}
                rows={4}
                onChange={({ target }) => this.setState({ seoDescription: target.value })}
              />
              <textarea
                placeholder='Long Form Keyword Optimized SEO Description'
                value={seoDescriptionLong}
                rows={12}
                onChange={({ target }) => this.setState({ seoDescriptionLong: target.value })}
              />
              <input
                placeholder={slugify(`Best in ${name}`).toLowerCase()}
                value={urlStub}
                onChange={({ target }) => this.setState({ urlStub: target.value })}
              />
            </div>
            {this.isDepartment() && (
              <div className='section'>
                <div className='section-label'>Google Product Taxonomy</div>
                <input
                  placeholder='Google Product Taxonomy (e.g. "Apparel & Accessories > Clothing > Dresses")'
                  value={googleProductCategory}
                  onChange={({ target }) => this.setState({ googleProductCategory: target.value })}
                />
                <div className='subcomment'>Used in product feeds for Meta, Google among others.</div>
              </div>
            )}
            <div className='section'>
              <div className='section-label'>Search Optimizations</div>
              {this.isCategory() && (
                <textarea
                  placeholder='Synonyms (comma separated, no spaces)'
                  value={synonyms}
                  rows={4}
                  onChange={({ target }) => this.setState({ synonyms: target.value })}
                />
              )}
            </div>
            <div className='section'>
              <div className='section-label'>Additional Configuration</div>
              <div className='checkbox-row'>
                <input
                  type='checkbox'
                  name='check'
                  checked={isFeatured}
                  onChange={e =>
                    this.setState({
                      isFeatured: e.target.checked
                    })
                  }
                />
                <div className='label'>Featured</div>
              </div>
              <div className='checkbox-row'>
                <input
                  type='checkbox'
                  name='check'
                  checked={isHidden}
                  onChange={e =>
                    this.setState({
                      isHidden: e.target.checked
                    })
                  }
                />
                <div className='label'>Hidden From Catalog Results</div>
              </div>
              <div className='checkbox-row'>
                <input
                  type='checkbox'
                  name='check'
                  checked={isHiddenForAllProductResults}
                  onChange={e =>
                    this.setState({
                      isHiddenForAllProductResults: e.target.checked
                    })
                  }
                />
                <div className='label'>Hidden For All Product Results</div>
              </div>
              <div className='checkbox-row'>
                <input
                  type='checkbox'
                  name='check'
                  checked={isAutoTag}
                  onChange={e =>
                    this.setState({
                      isAutoTag: e.target.checked
                    })
                  }
                />
                <div className='label'>Automatic Tagging: </div>
                <input
                  placeholder='Auto Tagging Min (Default 30%)'
                  className='field'
                  value={autoTagShopPercentageMin}
                  onChange={({ target }) => this.setState({ autoTagShopPercentageMin: target.value })}
                />
                <div className='label'>% Of Shop</div>
                <div className='subcomment'>(set to over 100 to only apply to brands)</div>
              </div>
              <div className='select-row'>
                <select
                  className='select'
                  value={this.state.shopProductResultsPageImageFormat}
                  onChange={({ target }) => this.setState({ shopProductResultsPageImageFormat: target.value })}
                >
                  <option value='default'>Default (Previews with every 7th as a Cover)</option>
                  <option value='preview'>Previews Only (White Background)</option>
                  <option value='cover'>Covers Only (Full Width With Images)</option>
                </select>
                <div className='label'>
                  Image Format on <a href={this.getShopSearchResultsPageUrl()}>Search Results Page</a>
                </div>
              </div>
              {selectedItemType === 'tag' && (
                <div className='radio-button' onClick={() => this.setState({ isFilter: !isFilter })}>
                  <div className='label'>Show Filter In Search</div>
                  <div className={classnames('radio', { selected: isFilter })}>✓</div>
                </div>
              )}
              <div className='action-btns'>
                <div onClick={this.deleteItem} className='action-btn delete'>
                  Delete
                </div>
                <div onClick={this.updateItem} className='action-btn delete'>
                  {isSaving ? 'Saving' : 'Save'}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className='empty-message'>Please make a selection on the left</div>
        )}
      </div>
    );
  }
}

export default HierarchyEdit;
